//#region imports
import { Auth } from 'aws-amplify';
import { ActivatedRoute, Router } from '@angular/router';
import { Component, HostBinding, OnInit, inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { VMOnlineLoginService } from '../login/vmonline-login.service';
import { Message } from 'primeng/api';
import { MfaType } from '../login/login.model';
import { LocalRoutes } from 'src/app/local-routes';
import { AuthService } from 'src/app/auth/auth.service';
import { MessageComponent } from '../../controls/message/message.component';
import { InputTextComponent } from '../../controls/input-text/input-text.component';

import { ButtonComponent } from '../../controls/buttons/button/button.component';

//#endregion imports

@Component({
    selector: 'app-authentication-code',
    templateUrl: './authentication-code.component.html',
    styleUrls: ['./authentication-code.component.scss'],
    imports: [
    MessageComponent,
    ReactiveFormsModule,
    InputTextComponent,
    ButtonComponent
],
})
export class AuthenticationCodeComponent implements OnInit {
	private route = inject(ActivatedRoute);
	private router = inject(Router);
	private loginService = inject(VMOnlineLoginService);
	private authService = inject(AuthService);

	@HostBinding('className') componentClass: string;

	//#region component declarations
	authenticationForm: UntypedFormGroup;
	code: string = null;
	messages: Message[];
	isProcessing = false;
	challengeName: MfaType = null;
	heading: string;
	subheading: string;

	readonly maxLength = 7;
	protected readonly MfaType = MfaType;

	//#endregion component declarations

	//#region constructor
	constructor() {
		this.componentClass = 'main-container';

		this.authenticationForm = new UntypedFormGroup({
			code: new UntypedFormControl(null, [Validators.required, Validators.maxLength(this.maxLength)]),
		});
	}
	//#endregion constructor

	ngOnInit(): void {
		this.challengeName = this.route.snapshot.paramMap.get('challengeName') as MfaType;

		if (!this.loginService.cognitoUser || this.challengeName === null) {
			this.router.navigate([LocalRoutes.LOGIN]);
		}

		if (this.challengeName === MfaType.SMS_MFA) {
			this.heading = 'SMS Authentication Code';
			this.subheading = 'Enter the 6-digit code sent to your phone';
		} else {
			this.heading = 'Authenticator Code';
			this.subheading = 'Enter the 6-digit code provided by the authenticator app';
		}
	}

	authenticationFailed(errorMessage: string, doLogin: boolean): void {
		this.messages = [];
		this.isProcessing = false;
		this.messages.push({
			severity: 'error',
			summary: null,
			detail: errorMessage,
		});

		if (doLogin) {
			setTimeout(() => {
				this.isProcessing = false;
				this.router.navigate([LocalRoutes.LOGIN]);
			}, 3000);
		}
	}

	verify(): void {
		const code = this.authenticationForm.controls.code.value.replace(/[^0-9]/g, ''); // Strip anything not a number (users often enter bonus spaces)
		this.isProcessing = true;
		this.messages = [];
		// congnito user pool setting
		this.authService.configureAmplify();

		Auth.confirmSignIn(this.loginService.cognitoUser, code, this.challengeName).then(
			(user) => {
				this.loginService.signInWithCognitoUser(user).subscribe((result) => {
					this.isProcessing = false;

					if (result.error) {
						this.authenticationFailed(result.error, false);
					}
				});
			},
			// error handler for AWS code verification
			(error) => {
				// if the user session has expired, there is no point keeping
				// the user on authenticator screen since user has to Login again.
				if (error?.code === 'NotAuthorizedException') {
					this.authenticationFailed(
						`Your session has expired.
					We are redirecting you to the login page`,
						true
					);
				} else if (
					error?.code === 'EnableSoftwareTokenMFAException' ||
					error?.code === 'InvalidParameterException' ||
					error?.code === 'CodeMismatchException'
				) {
					this.authenticationFailed(
						`The code you have entered is incorrect.
					Please enter the correct code from your authenticator app`,
						false
					);
				} else {
					this.authenticationFailed(
						`Something went wrong in verifying authentication code.
						We are redirecting you to the login page.`,
						true
					);
				}
			}
		);
	}
}
